import { IJobDetail, WorkType } from '@/types/jobs';
import { getJobDisplayTitle } from '@/utils/jobCardHelper';
import { generateJobDetailsURL, getCanonicalOrigin } from '@/utils/jobListingHelper';
import { resolveUrl } from '@/utils/jsonLd';
import dayjs from 'dayjs';

const EMPLOYMENT_TYPE_MAP: Record<WorkType, string> = {
  Locum: 'TEMPORARY',
  Permanent: 'FULL_TIME',
};

export const mapWorkTypeToEmploymentType = (workType?: WorkType): string =>
  (workType && EMPLOYMENT_TYPE_MAP[workType]) || 'OTHER';

const CURRENCY_BY_COUNTRY: Record<string, string> = {
  AU: 'AUD',
  NZ: 'NZD',
};

const getCurrency = (country?: string): string =>
  (country && CURRENCY_BY_COUNTRY[country]) || 'AUD';

const PAY_INTERVAL_UNIT_MAP: Array<[RegExp, string]> = [
  [/hour/i, 'HOUR'],
  [/day/i, 'DAY'],
  [/week/i, 'WEEK'],
  [/month/i, 'MONTH'],
  [/(year|annum)/i, 'YEAR'],
];

const getUnitTextFromPayInterval = (payInterval: string | undefined, fallback: string): string => {
  const match = payInterval && PAY_INTERVAL_UNIT_MAP.find(([pattern]) => pattern.test(payInterval));
  return match ? match[1] : fallback;
};

type JsonLdMonetaryAmount = {
  '@type': 'MonetaryAmount';
  currency: string;
  value: {
    '@type': 'QuantitativeValue';
    value?: number;
    minValue?: number;
    maxValue?: number;
    unitText: string;
  };
};

// The API types these fields as `number`, but has been observed sending numeric strings
// (e.g. guaranteedHourlyRate: "0") at runtime. Coercing defensively here also guards against
// a "0" string being treated as a truthy guaranteed rate and shadowing a real salary range,
// and ensures the JSON-LD emits a Number rather than a Text value, per Google's JobPosting spec.
const toPositiveNumber = (value: unknown): number | undefined => {
  const numeric = Number(value);
  return typeof value !== 'boolean' && Number.isFinite(numeric) && numeric > 0
    ? numeric
    : undefined;
};

const buildBaseSalary = (job: IJobDetail): JsonLdMonetaryAmount | undefined => {
  const currency = getCurrency(job.country);

  const guaranteedHourlyRate = toPositiveNumber(job.guaranteedHourlyRate);
  if (guaranteedHourlyRate) {
    return {
      '@type': 'MonetaryAmount',
      currency,
      value: { '@type': 'QuantitativeValue', value: guaranteedHourlyRate, unitText: 'HOUR' },
    };
  }

  const payRate = toPositiveNumber(job.payRate);
  if (job.workType === 'Locum' && payRate) {
    return {
      '@type': 'MonetaryAmount',
      currency,
      value: {
        '@type': 'QuantitativeValue',
        value: payRate,
        unitText: getUnitTextFromPayInterval(job.payInterval, 'DAY'),
      },
    };
  }

  const salaryFrom = toPositiveNumber(job.salaryFrom);
  const salaryTo = toPositiveNumber(job.salaryTo);
  if (salaryFrom && salaryTo) {
    return {
      '@type': 'MonetaryAmount',
      currency,
      value: {
        '@type': 'QuantitativeValue',
        minValue: salaryFrom,
        maxValue: salaryTo,
        unitText: 'YEAR',
      },
    };
  }

  return undefined;
};

// Returns undefined if the date can't be parsed, so callers can skip publishing structured
// data for a bad record rather than throw and take out the whole page render.
const toValidIsoDateTime = (date?: string): string | undefined => {
  if (!date) {
    return undefined;
  }
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.toISOString() : undefined;
};

// Date-only (no time-of-day component), so converting to UTC on the server can't shift the
// calendar day backwards for timezones east of UTC (e.g. AEST/NZDT).
const toValidDateOnlyString = (date?: string): string | undefined => {
  if (!date) {
    return undefined;
  }
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : undefined;
};

const getSpecialityForUrl = (job: IJobDetail) =>
  job.subspeciality ? job.subspeciality : job.speciality;

/**
 * Builds a schema.org JobPosting object for a job card, per A2P-4345.
 * baseSalary is omitted when the job has no derivable salary data (e.g. "Available on
 * application") rather than fabricating a placeholder value. Returns undefined when the
 * job has no parseable datePosted, since that field is required for a valid JobPosting.
 */
export const buildJobPostingJsonLd = (job: IJobDetail, origin: string = getCanonicalOrigin()) => {
  const datePosted = toValidIsoDateTime(job.createdAt);
  if (!datePosted) {
    return undefined;
  }

  const hiringOrganizationName =
    job.displayClientName && job.accountName ? job.accountName : 'Medrecruit';
  const baseSalary = buildBaseSalary(job);
  const validThrough = toValidDateOnlyString(job.endDate);
  const url = resolveUrl(
    origin,
    generateJobDetailsURL(job.jobNumber, job.grade, getSpecialityForUrl(job)),
  );

  return {
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    // Matches the visible card heading; title is required, so fall back to the free-text title.
    title: getJobDisplayTitle(job) || job.title,
    description: job.description ?? job.descriptionPlainText ?? job.title,
    datePosted,
    ...(validThrough ? { validThrough } : {}),
    url,
    employmentType: mapWorkTypeToEmploymentType(job.workType),
    hiringOrganization: {
      '@type': 'Organization',
      name: hiringOrganizationName,
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        ...(job.city ? { addressLocality: job.city } : {}),
        ...(job.locationDisplay ? { addressRegion: job.locationDisplay } : {}),
        ...(job.country ? { addressCountry: job.country } : {}),
      },
    },
    ...(baseSalary ? { baseSalary } : {}),
  };
};
